/**
 * DropCard Component - Displays drop information in a card format
 */

import React, { useState } from "react";
import { Card, Tag, Space, Typography, Button, Progress, message } from "antd";
import {
  EnvironmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { format } from "date-fns";

const { Text } = Typography;

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
  transition: box-shadow 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const StatusTag = styled(Tag)`
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 4px;
`;

const DropInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const getStatusColor = (status) => {
  const colors = {
    pending: "default",
    arrived: "processing",
    completed: "success",
  };
  return colors[status] || "default";
};

const getStatusIcon = (status) => {
  const icons = {
    pending: <ClockCircleOutlined />,
    arrived: <PlayCircleOutlined />,
    completed: <CheckCircleOutlined />,
  };
  return icons[status] || <ClockCircleOutlined />;
};

/**
 * Check if drop can be completed
 * @param {Object} drop - Drop object
 * @returns {boolean} True if all orders have itemsDelivered === itemsTotal
 */
const canCompleteDrop = (drop) => {
  if (!drop.orders || drop.orders.length === 0) {
    return false; // No orders to complete
  }

  // Check all orders have itemsDelivered === itemsTotal
  const allItemsDelivered = drop.orders.every((order) => {
    const itemsTotal = order.itemsTotal || 0;
    const itemsDelivered = order.itemsDelivered || 0;
    return itemsDelivered === itemsTotal && itemsTotal > 0;
  });

  return allItemsDelivered;
};

const DropCard = ({
  drop,
  onView,
  onEdit,
  onDelete,
  onComplete,
  onUpdateProgress,
  showActions = true,
}) => {
  const [copying, setCopying] = useState(false);
  const statusColor = getStatusColor(drop.status);
  const statusIcon = getStatusIcon(drop.status);

  const totalItems =
    drop.orders?.reduce((sum, order) => sum + (order.itemsTotal || 0), 0) || 0;
  const deliveredItems =
    drop.orders?.reduce((sum, order) => sum + (order.itemsDelivered || 0), 0) ||
    0;
  const progressPercentage =
    totalItems > 0 ? Math.round((deliveredItems / totalItems) * 100) : 0;

  const isReadyToComplete = canCompleteDrop(drop);

  const handleCopyCustomerDetails = async () => {
    if (!drop.orders || drop.orders.length === 0) {
      message.warning("No orders in this drop");
      return;
    }

    try {
      setCopying(true);

      // Extract full order objects from drop.orders
      const orders = drop.orders
        .map((dropOrder) => {
          // If dropOrder is an object with order property, use that
          if (typeof dropOrder === "object" && dropOrder.order) {
            return dropOrder.order;
          }
          // If dropOrder is just an orderId string, we can't get full details
          // Return null to filter out
          return null;
        })
        .filter(Boolean);

      if (orders.length === 0) {
        message.warning(
          "Order details not available. Please ensure orders are fully loaded."
        );
        return;
      }

      const { formatMultipleCustomerDetails } = await import(
        "../../../shared/utils/customerDetailsFormatter"
      );
      const { copyToClipboard } = await import(
        "../../leads/services/communicationTemplateService"
      );

      const details = formatMultipleCustomerDetails(orders);
      const success = await copyToClipboard(details);

      if (success) {
        message.success("Customer details copied to clipboard!");
      } else {
        message.error("Failed to copy customer details");
      }
    } catch (error) {
      console.error("Error copying customer details:", error);
      message.error("Failed to copy customer details");
    } finally {
      setCopying(false);
    }
  };

  return (
    <StyledCard
      title={
        <Space>
          <EnvironmentOutlined />
          <Text strong>
            Stop {drop.sequence}: {drop.location?.city || "Unknown"}
          </Text>
          <StatusTag color={statusColor} icon={statusIcon}>
            {drop.status?.toUpperCase() || "PENDING"}
          </StatusTag>
        </Space>
      }
      extra={
        showActions && (
          <Space>
            {drop.orders && drop.orders.length > 0 && (
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopyCustomerDetails}
                loading={copying}
                title="Copy customer details for all orders"
              >
                Copy Customer Details
              </Button>
            )}
            {onUpdateProgress && drop.status !== "completed" && (
              <Button
                type="link"
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => onUpdateProgress(drop)}
                title="Update delivery progress for orders"
              >
                Update Progress
              </Button>
            )}
            {onEdit && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(drop)}
              >
                Edit
              </Button>
            )}
            {onComplete && drop.status !== "completed" && (
              <Button
                type="primary"
                size="small"
                onClick={() => onComplete(drop)}
                disabled={!isReadyToComplete}
                title={
                  !isReadyToComplete
                    ? "Cannot complete: Not all items delivered. All orders must have itemsDelivered equal to itemsTotal."
                    : "Complete drop"
                }
              >
                Complete
              </Button>
            )}
            {onDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(drop)}
              >
                Delete
              </Button>
            )}
          </Space>
        )
      }
    >
      <DropInfo>
        <InfoRow>
          <Text strong>Location:</Text>
          <Text>{drop.location?.address || drop.location?.city || "N/A"}</Text>
        </InfoRow>
        <InfoRow>
          <Text strong>Province:</Text>
          <Text>{drop.location?.province || "N/A"}</Text>
        </InfoRow>
        <InfoRow>
          <Text strong>Orders:</Text>
          <Text>{drop.orders?.length || 0}</Text>
        </InfoRow>

        {/* Display customer information for each order */}
        {drop.orders && drop.orders.length > 0 && (
          <div style={{ marginTop: 8, marginLeft: 0 }}>
            {drop.orders.map((dropOrder, index) => {
              const order =
                typeof dropOrder === "object" && dropOrder.order
                  ? dropOrder.order
                  : null;
              const orderId =
                typeof dropOrder === "string"
                  ? dropOrder
                  : dropOrder.orderId || order?.id || `Order ${index + 1}`;
              const customerName = order?.customer || "Unknown Customer";
              const itemsDelivered = dropOrder.itemsDelivered || 0;
              const itemsTotal = dropOrder.itemsTotal || 0;

              return (
                <InfoRow
                  key={orderId || index}
                  style={{ marginTop: 4, marginLeft: 16 }}
                >
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    • {orderId} - {customerName}
                    {itemsTotal > 0 && (
                      <Text
                        type="secondary"
                        style={{ fontSize: "11px", marginLeft: 8 }}
                      >
                        ({itemsDelivered}/{itemsTotal} items)
                      </Text>
                    )}
                  </Text>
                </InfoRow>
              );
            })}
          </div>
        )}

        {drop.completedAt && (
          <InfoRow>
            <Text strong>Completed:</Text>
            <Text>
              {format(new Date(drop.completedAt), "MMM dd, yyyy HH:mm")}
            </Text>
          </InfoRow>
        )}
        {drop.driverConfirmation?.driverId && (
          <InfoRow>
            <Text strong>Confirmed by:</Text>
            <Text>{drop.driverConfirmation.driverId}</Text>
          </InfoRow>
        )}
      </DropInfo>

      {totalItems > 0 && (
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Delivery Progress
          </Text>
          <Progress
            percent={progressPercentage}
            status={drop.status === "completed" ? "success" : "active"}
            strokeColor={drop.status === "completed" ? "#52c41a" : "#1890ff"}
          />
          <Text type="secondary" style={{ fontSize: "11px" }}>
            {deliveredItems} / {totalItems} items delivered
          </Text>
        </div>
      )}

      {drop.driverConfirmation?.notes && (
        <div style={{ marginTop: "12px" }}>
          <Text
            type="secondary"
            style={{ fontSize: "12px", fontStyle: "italic" }}
          >
            Notes: {drop.driverConfirmation.notes}
          </Text>
        </div>
      )}
    </StyledCard>
  );
};

export default DropCard;
